import React, { useState, useEffect, useCallback } from 'react';
import {
  UsersIcon,
  ShoppingBagIcon,
  CurrencyDollarIcon,
  CheckCircleIcon,
  ClockIcon,
  ExclamationTriangleIcon,
  ArrowRightIcon,
  BanknotesIcon,
  TruckIcon,
  ArrowPathIcon,
  FireIcon,
  ChartBarIcon,
  BuildingStorefrontIcon,
} from '@heroicons/react/24/outline';
import { useNavigate } from 'react-router-dom';
import { fetchMarketplaceOverview } from '../api/superAdminApi';

const MarketplaceOverviewPage: React.FC = () => {
  const navigate = useNavigate();
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await fetchMarketplaceOverview();
      setData(response.data || response);
    } catch (err: any) {
      setError(err.message || 'Failed to load overview');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const fmt = (n: number) => new Intl.NumberFormat('en-RW', { style: 'currency', currency: 'RWF', minimumFractionDigits: 0 }).format(n || 0);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <div className="relative">
            <div className="w-16 h-16 border-4 border-blue-200 rounded-full"></div>
            <div className="w-16 h-16 border-4 border-blue-600 rounded-full border-t-transparent animate-spin absolute top-0 left-0"></div>
          </div>
          <p className="text-gray-500 mt-4 font-medium">Loading marketplace data...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center max-w-md">
          <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto">
            <ExclamationTriangleIcon className="w-10 h-10 text-red-500" />
          </div>
          <h3 className="text-lg font-semibold text-gray-900 mt-4">Something went wrong</h3>
          <p className="text-gray-500 mt-2">{error}</p>
          <button onClick={fetchData} className="mt-6 px-6 py-2.5 bg-blue-600 text-white rounded-xl hover:bg-blue-700 font-medium transition-colors shadow-lg shadow-blue-200">
            Try Again
          </button>
        </div>
      </div>
    );
  }

  const stats = [
    { label: 'Buyer Orgs', value: data?.totalBuyerOrgs ?? data?.total_buyer_orgs ?? 0, icon: UsersIcon, gradient: 'from-blue-500 to-blue-600', shadow: 'shadow-blue-200' },
    { label: 'Suppliers', value: data?.totalSupplierOrgs ?? data?.total_supplier_orgs ?? 0, icon: BuildingStorefrontIcon, gradient: 'from-emerald-500 to-emerald-600', shadow: 'shadow-emerald-200' },
    { label: 'Total Orders', value: data?.totalOrders ?? data?.total_orders ?? 0, icon: ShoppingBagIcon, gradient: 'from-violet-500 to-violet-600', shadow: 'shadow-violet-200' },
    { label: 'GMV', value: fmt(data?.grossMerchandiseValue ?? data?.gross_merchandise_value ?? 0), icon: CurrencyDollarIcon, gradient: 'from-amber-500 to-orange-500', shadow: 'shadow-amber-200' },
    { label: 'Commissions', value: fmt(data?.totalCommissions ?? data?.total_commissions ?? 0), icon: BanknotesIcon, gradient: 'from-rose-500 to-rose-600', shadow: 'shadow-rose-200' },
    { label: 'Settlements Paid', value: fmt(data?.totalSettlementsPaid ?? data?.total_settlements_paid ?? 0), icon: CheckCircleIcon, gradient: 'from-teal-500 to-teal-600', shadow: 'shadow-teal-200' },
  ];

  const queues = [
    { label: 'Pending Verification', count: data?.pendingVerification ?? data?.pending_verification ?? 0, icon: ClockIcon, color: 'text-amber-600', bg: 'bg-amber-50', border: 'border-amber-200 hover:border-amber-300', path: '/marketplace/admin/verification' },
    { label: 'Pending Orders', count: data?.pendingOrders ?? data?.pending_orders ?? 0, icon: ShoppingBagIcon, color: 'text-blue-600', bg: 'bg-blue-50', border: 'border-blue-200 hover:border-blue-300', path: '/marketplace/admin/orders' },
    { label: 'In Transit', count: data?.inTransitOrders ?? data?.in_transit_orders ?? 0, icon: TruckIcon, color: 'text-indigo-600', bg: 'bg-indigo-50', border: 'border-indigo-200 hover:border-indigo-300', path: '/marketplace/admin/orders' },
    { label: 'Pending Settlements', count: data?.pendingSettlements ?? data?.pending_settlements ?? 0, icon: CurrencyDollarIcon, color: 'text-emerald-600', bg: 'bg-emerald-50', border: 'border-emerald-200 hover:border-emerald-300', path: '/marketplace/admin/finance' },
    { label: 'Disputed Orders', count: data?.disputedOrders ?? data?.disputed_orders ?? 0, icon: ExclamationTriangleIcon, color: 'text-red-600', bg: 'bg-red-50', border: 'border-red-200 hover:border-red-300', path: '/marketplace/admin/orders' },
  ];

  const recentOrders = data?.recentOrders ?? data?.recent_orders ?? [];
  const recentActivity = data?.recentActivity ?? data?.recent_activity ?? [];

  return (
    <div className="space-y-8">
      {/* Hero Header */}
      <div className="relative overflow-hidden bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 rounded-2xl p-8 text-white">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 right-0 w-96 h-96 bg-white rounded-full -translate-y-1/2 translate-x-1/3"></div>
          <div className="absolute bottom-0 left-0 w-64 h-64 bg-white rounded-full translate-y-1/2 -translate-x-1/4"></div>
        </div>
        <div className="relative flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-extrabold tracking-tight">Marketplace Overview</h1>
            <p className="text-blue-100 mt-2 text-lg">Real-time platform metrics and activity</p>
          </div>
          <button onClick={fetchData} className="flex items-center gap-2 px-5 py-2.5 bg-white/20 backdrop-blur-sm text-white rounded-xl hover:bg-white/30 font-medium transition-all">
            <ArrowPathIcon className="w-5 h-5" />
            Refresh
          </button>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {stats.map((stat) => (
          <div key={stat.label} className={`relative overflow-hidden bg-white rounded-2xl border border-gray-100 p-6 shadow-lg ${stat.shadow} hover:shadow-xl transition-shadow`}>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500 uppercase tracking-wide">{stat.label}</p>
                <p className="text-3xl font-extrabold text-gray-900 mt-2">{stat.value}</p>
              </div>
              <div className={`w-14 h-14 rounded-2xl bg-gradient-to-br ${stat.gradient} flex items-center justify-center shadow-lg`}>
                <stat.icon className="w-7 h-7 text-white" />
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Attention Queues */}
      <div>
        <div className="flex items-center gap-3 mb-5">
          <div className="w-10 h-10 bg-gradient-to-br from-orange-400 to-red-500 rounded-xl flex items-center justify-center">
            <FireIcon className="w-5 h-5 text-white" />
          </div>
          <h2 className="text-xl font-bold text-gray-900">Requires Attention</h2>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {queues.map((q) => (
            <button
              key={q.label}
              onClick={() => navigate(q.path)}
              className={`group flex items-center justify-between p-5 rounded-2xl border ${q.border} ${q.bg} hover:shadow-lg transition-all text-left`}
            >
              <div className="flex items-center gap-4">
                <div className={`w-12 h-12 rounded-xl ${q.bg} flex items-center justify-center`}>
                  <q.icon className={`w-6 h-6 ${q.color}`} />
                </div>
                <div>
                  <p className="text-sm font-semibold text-gray-700">{q.label}</p>
                  <p className={`text-2xl font-extrabold ${q.color} mt-0.5`}>{q.count}</p>
                </div>
              </div>
              <ArrowRightIcon className="w-5 h-5 text-gray-400 group-hover:text-gray-600 group-hover:translate-x-1 transition-all" />
            </button>
          ))}
        </div>
      </div>

      {/* Recent Orders + Activity */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent Orders */}
        {recentOrders.length > 0 && (
          <div className="bg-white rounded-2xl border border-gray-100 shadow-lg overflow-hidden">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
              <h3 className="font-bold text-gray-900">Recent Orders</h3>
              <button onClick={() => navigate('/marketplace/admin/orders')} className="text-sm text-blue-600 hover:text-blue-700 font-semibold">
                View all →
              </button>
            </div>
            <div className="divide-y divide-gray-50">
              {recentOrders.slice(0, 5).map((order: any, idx: number) => (
                <div key={order.id || idx} className="flex items-center justify-between px-6 py-3 hover:bg-gray-50 transition-colors">
                  <div>
                    <p className="font-semibold text-gray-900 text-sm">{order.orderNumber || order.order_number}</p>
                    <p className="text-xs text-gray-500">{order.buyerOrganisation || order.buyer_org_name}</p>
                  </div>
                  <div className="text-right">
                    <p className="font-bold text-gray-900 text-sm">{fmt(order.totalAmount || order.total_amount || 0)}</p>
                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                      order.status === 'pending' ? 'bg-amber-100 text-amber-700' :
                      order.status === 'confirmed' ? 'bg-blue-100 text-blue-700' :
                      order.status === 'in_transit' ? 'bg-indigo-100 text-indigo-700' :
                      order.status === 'delivered' ? 'bg-emerald-100 text-emerald-700' :
                      'bg-gray-100 text-gray-600'
                    }`}>
                      {(order.status || 'pending').replace('_', ' ')}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Recent Activity */}
        {recentActivity.length > 0 && (
          <div className="bg-white rounded-2xl border border-gray-100 shadow-lg overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-100">
              <h3 className="font-bold text-gray-900">Recent Activity</h3>
            </div>
            <div className="divide-y divide-gray-50">
              {recentActivity.slice(0, 5).map((activity: any, idx: number) => (
                <div key={idx} className="flex items-center gap-3 px-6 py-3 hover:bg-gray-50 transition-colors">
                  <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${
                    activity.type === 'order' ? 'bg-blue-100' :
                    activity.type === 'verification' ? 'bg-emerald-100' :
                    activity.type === 'settlement' ? 'bg-amber-100' :
                    'bg-gray-100'
                  }`}>
                    {activity.type === 'order' ? <ShoppingBagIcon className="w-4 h-4 text-blue-600" /> :
                     activity.type === 'verification' ? <CheckCircleIcon className="w-4 h-4 text-emerald-600" /> :
                     activity.type === 'settlement' ? <CurrencyDollarIcon className="w-4 h-4 text-amber-600" /> :
                     <ChartBarIcon className="w-4 h-4 text-gray-600" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-gray-700 truncate">{activity.description || activity.event_type}</p>
                    <p className="text-xs text-gray-400">{activity.timestamp || activity.created_at}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Empty state if no data */}
        {recentOrders.length === 0 && recentActivity.length === 0 && (
          <div className="lg:col-span-2 bg-white rounded-2xl border border-gray-100 shadow-lg p-12 text-center">
            <div className="w-20 h-20 bg-gradient-to-br from-blue-100 to-indigo-100 rounded-2xl flex items-center justify-center mx-auto">
              <ChartBarIcon className="w-10 h-10 text-blue-500" />
            </div>
            <h3 className="text-lg font-bold text-gray-900 mt-4">No recent activity</h3>
            <p className="text-gray-500 mt-2 max-w-sm mx-auto">Activity will appear here as orders are placed and suppliers are verified.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default MarketplaceOverviewPage;
